import React, { useState } from 'react';
import { X, CheckCircle, AlertCircle, ExternalLink, Info } from 'lucide-react';
import { TwitterIntegrationFactory } from '../../services/integrations/social/TwitterIntegrationFactory';

interface TwitterIntegrationSetupProps {
  onIntegrationCreated: (integration: any) => void;
  onCancel: () => void;
}

export const TwitterIntegrationSetup: React.FC<TwitterIntegrationSetupProps> = ({
  onIntegrationCreated,
  onCancel
}) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [testResult, setTestResult] = useState<any>(null);

  // Form data
  const [formData, setFormData] = useState({
    name: 'Twitter (X) Marketing',
    bearerToken: '',
    apiKey: '',
    apiSecret: '',
    accessToken: '',
    accessTokenSecret: '',
    enableTweeting: true,
    enableAnalytics: true,
    enableThreads: true,
    maxTweetsPerHour: 50,
    autoSplitLongContent: true
  });

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const handleTestCredentials = async () => {
    if (!formData.apiKey || !formData.apiSecret) {
      setError('Please fill in at least API Key and API Secret');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const credentials: any = {
        apiKey: formData.apiKey,
        apiSecret: formData.apiSecret
      };

      if (formData.bearerToken) {
        credentials.bearerToken = formData.bearerToken;
      }

      if (formData.accessToken && formData.accessTokenSecret) {
        credentials.accessToken = formData.accessToken;
        credentials.accessTokenSecret = formData.accessTokenSecret;
      }

      const result = await TwitterIntegrationFactory.testCredentials(credentials);

      setTestResult(result);

      if (result.success) {
        setStep(2);
      } else {
        setError(result.message || 'Failed to validate credentials');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to test credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateIntegration = async () => {
    setLoading(true);
    setError('');

    try {
      const integration = await TwitterIntegrationFactory.createFromWizard(formData);
      onIntegrationCreated(integration);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to create integration');
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-content mb-2">Twitter API Credentials</h3>
        <p className="text-content-secondary mb-4">
          Enter your Twitter (X) API credentials to connect your Twitter account.
        </p>
        
        <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg mb-6">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 dark:text-blue-300 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 dark:text-blue-300">Need Twitter API credentials?</h4>
              <p className="text-blue-800 dark:text-blue-300 text-sm mt-1">
                Apply for Twitter API access and create an application in the Twitter Developer Portal.
              </p>
              <a
                href="https://developer.twitter.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-300 hover:text-blue-700 dark:hover:text-blue-300 text-sm mt-2"
              >
                Twitter Developer Portal
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Integration Name
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Twitter (X) Marketing"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            API Key (Consumer Key) *
          </label>
          <input
            type="text"
            value={formData.apiKey}
            onChange={(e) => handleInputChange('apiKey', e.target.value)}
            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter your Twitter API Key"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            API Secret (Consumer Secret) *
          </label>
          <input
            type="password"
            value={formData.apiSecret}
            onChange={(e) => handleInputChange('apiSecret', e.target.value)}
            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter your Twitter API Secret"
          />
        </div>

        <div className="bg-gray-50 dark:bg-dark-700 p-4 rounded-lg">
          <h4 className="font-medium text-content mb-2">Optional: Enhanced Features</h4>
          <p className="text-sm text-content-secondary mb-3">
            Provide additional credentials for posting and enhanced analytics:
          </p>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Bearer Token (for analytics)
              </label>
              <input
                type="password"
                value={formData.bearerToken}
                onChange={(e) => handleInputChange('bearerToken', e.target.value)}
                className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your Bearer Token (optional)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Access Token (for posting)
              </label>
              <input
                type="password"
                value={formData.accessToken}
                onChange={(e) => handleInputChange('accessToken', e.target.value)}
                className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your Access Token (optional)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Access Token Secret (for posting)
              </label>
              <input
                type="password"
                value={formData.accessTokenSecret}
                onChange={(e) => handleInputChange('accessTokenSecret', e.target.value)}
                className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your Access Token Secret (optional)"
              />
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-300" />
          <span className="text-red-700 dark:text-red-300 text-sm">{error}</span>
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-content mb-2">Configuration</h3>
        <p className="text-content-secondary mb-4">
          Configure your Twitter integration settings and preferences.
        </p>
      </div>

      {testResult?.userProfile && (
        <div className="bg-green-50 dark:bg-green-900/30 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-300" />
            <span className="font-medium text-green-900 dark:text-green-300">Connected Successfully</span>
          </div>
          <p className="text-green-800 dark:text-green-300 text-sm">
            Connected as: @{testResult.userProfile.username} ({testResult.userProfile.name})
          </p>
          {testResult.capabilities && (
            <p className="text-green-800 dark:text-green-300 text-sm">
              Available features: {testResult.capabilities.join(', ')}
            </p>
          )}
        </div>
      )}

      <div className="space-y-4">
        <div>
          <h4 className="font-medium text-content mb-3">Features</h4>
          <div className="space-y-3">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formData.enableTweeting}
                onChange={(e) => handleInputChange('enableTweeting', e.target.checked)}
                className="w-4 h-4 text-blue-600 dark:text-blue-300 border-line-strong rounded focus:ring-blue-500"
              />
              <div>
                <span className="font-medium text-content">Tweet Publishing</span>
                <p className="text-sm text-content-secondary">Enable posting tweets to Twitter</p>
              </div>
            </label>

            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formData.enableAnalytics}
                onChange={(e) => handleInputChange('enableAnalytics', e.target.checked)}
                className="w-4 h-4 text-blue-600 dark:text-blue-300 border-line-strong rounded focus:ring-blue-500"
              />
              <div>
                <span className="font-medium text-content">Analytics & Engagement Tracking</span>
                <p className="text-sm text-content-secondary">Track tweet performance and engagement metrics</p>
              </div>
            </label>

            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formData.enableThreads}
                onChange={(e) => handleInputChange('enableThreads', e.target.checked)}
                className="w-4 h-4 text-blue-600 dark:text-blue-300 border-line-strong rounded focus:ring-blue-500"
              />
              <div>
                <span className="font-medium text-content">Thread Creation</span>
                <p className="text-sm text-content-secondary">Enable creating Twitter threads for long content</p>
              </div>
            </label>

            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formData.autoSplitLongContent}
                onChange={(e) => handleInputChange('autoSplitLongContent', e.target.checked)}
                className="w-4 h-4 text-blue-600 dark:text-blue-300 border-line-strong rounded focus:ring-blue-500"
              />
              <div>
                <span className="font-medium text-content">Auto-split Long Content</span>
                <p className="text-sm text-content-secondary">Automatically split content longer than 280 characters into threads</p>
              </div>
            </label>
          </div>
        </div>

        <div>
          <h4 className="font-medium text-content mb-3">Rate Limiting</h4>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Maximum Tweets per Hour
            </label>
            <input
              type="number"
              min="1"
              max="300"
              value={formData.maxTweetsPerHour}
              onChange={(e) => handleInputChange('maxTweetsPerHour', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-sm text-content-secondary mt-1">
              Twitter API allows up to 300 tweets per hour. Set a lower limit to avoid rate limiting.
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-300" />
          <span className="text-red-700 dark:text-red-300 text-sm">{error}</span>
        </div>
      )}
    </div>
  );

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-content">Twitter (X) Integration Setup</h2>
          <p className="text-content-secondary">Connect your Twitter account</p>
        </div>
        <button aria-label="Close"
          onClick={onCancel}
          className="p-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center mb-8">
        <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
          step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-dark-600 text-content-secondary'
        }`}>
          1
        </div>
        <div className={`flex-1 h-1 mx-4 ${
          step >= 2 ? 'bg-blue-600' : 'bg-gray-200 dark:bg-dark-600'
        }`} />
        <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
          step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-dark-600 text-content-secondary'
        }`}>
          2
        </div>
      </div>

      {/* Step Content */}
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}

      {/* Actions */}
      <div className="flex items-center justify-between pt-6 border-t">
        <div className="text-sm text-content-secondary">
          {step === 1 && 'Step 1 of 2: Credentials'}
          {step === 2 && 'Step 2 of 2: Configuration'}
        </div>
        
        <div className="flex items-center gap-3">
          {step === 2 && (
            <button
              onClick={() => setStep(1)}
              className="px-4 py-2 border border-line-strong text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700"
              disabled={loading}
            >
              Back
            </button>
          )}
          
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-line-strong text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700"
            disabled={loading}
          >
            Cancel
          </button>
          
          {step === 1 && (
            <button
              onClick={handleTestCredentials}
              disabled={loading || !formData.apiKey || !formData.apiSecret}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? 'Testing...' : 'Test & Continue'}
            </button>
          )}
          
          {step === 2 && (
            <button
              onClick={handleCreateIntegration}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? 'Creating...' : 'Create Integration'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};